import removeNull from '@/api/lib/helpers/removeNull';
import diff from '@/lib/helpers/diff';
import clone from '@/lib/helpers/clone';
import dialog from '@/lib/helpers/dialog';
import { looksLikeBaseModel } from '@/models/BaseModel';

export default {
    data: () => ({
        id: 'Form',
        form: null,
        model: null,
        model_id: null,
        modelGetRequest: null,
        updateRequestMethod: 'PUT',
        requestConfig: { base_url: null },
        original: null,
        updateCacheWithGetAfterSubmit: true,
        disabled: false,
        formRequest: null
    }),
    props: {
        value: {
            type: Object,
            default: null
        },
        hideActions: {
            type: Boolean,
            default: false
        },
        formId: {
            type: String,
            default: null
        },
        hideHeader: {
            type: Boolean,
            default: false
        },
        prependCancelButton: {
            type: Boolean,
            default: false
        },
        editing: {
            type: String,
            default: null
        },
        returnId: {
            type: Boolean,
            default: false
        },
        returnFields: {
            type: Array,
            default: null
        },
        returnModel: {
            type: Boolean,
            default: false
        },
        requestParams: {
            type: Object,
            default: () => ({})
        },
        preset: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        isEditing() {
            return !!this.model_id;
        },
        hasPreset() {
            return this.preset && Object.keys(this.preset).length > 0;
        }
    },
    beforeDestroy() {
        this.$root.$off(`${this.id}/onSubmit`);
        this.$root.$off(`${this.id}/onValidate`);
    },
    watch: {
        preset: {
            handler(preset) {
                if (preset && this.form) {
                    this.form.$fill(preset);
                    this.original = clone(this.form);
                }
            },
            deep: true,
            immediate: false
        }
    },
    methods: {
        async setup() {
            try {
                if (this.formId !== null) {
                    this.id = this.formId;
                }

                if (this.id === 'Form') {
                    console.warn(
                        'You should not use the default id "Form" for your form component. Please use a different id.'
                    );
                }

                if (!this.model) {
                    console.warn(
                        'You should define the model for your form component. Please set this.model to the model class.'
                    );
                }

                this.form = new this.model();

                if (this.editing) {
                    this.model_id = this.editing;

                    if (this.modelGetRequest) {
                        this.form = await this.modelGetRequest(this.model_id);
                    } else {
                        this.form = await this.model.$get(this.model_id);
                    }

                    if (!this.form) {
                        // this.form = new this.model();
                        throw new Error(`Could not find ${this.model.name} with id ${this.model_id}`);
                    }
                }

                if (!this.editing && this.value instanceof this.model) {
                    this.form.$fill(this.value);
                }

                if (!this.editing && typeof this.withHistory !== 'undefined' && this.withHistory) {
                    this.registerHistory(this.form);
                }

                if (this.hasPreset) {
                    this.form.$fill(this.preset);
                }

                this.original = clone(this.form);

                this.$root.$on(`${this.id}/onSubmit`, params => {
                    this.$refs.form.onSubmit(params);
                });

                this.$root.$on(`${this.id}/onValidate`, () => {
                    this.$refs.form.onValidate();
                });

                await this.afterSetup();
            } catch (error) {
                this.form = null;
                this.original = null;
                this.handleError(error, () => this.setup());
            }
        },
        async afterSetup() {
            return Promise.resolve();
        },
        emit(name, data) {
            if (name === 'onSubmit') {
                data = removeNull(data);
            }

            this.$root.$emit(`${this.id}/${name}`, data);
        },
        async resetForm(force) {
            if (force || this._.isNil(this.value)) {
                this.form = null;
                this.original = null;

                await this.$nextTick();

                this.form = new this.model();
                this.original = clone(this.form);
            } else if (this.value) {
                this.form = null;
                this.original = null;

                await this.$nextTick();

                if (this.value instanceof this.model) {
                    this.form.$fill(this.value);
                }

                this.original = clone(this.form);
            }

            await this.afterReset();
        },
        async afterReset() {
            // do nothing
        },
        onSuccess(form) {
            this.$emit('success', form);
            this.emit('onSuccess', form);
        },
        onError(data) {
            this.$emit('error', data);
            this.emit('onError', data);
        },
        onCancel() {
            this.$emit('cancel');
            this.resetForm(true);
        },
        onFormError(errors) {
            this._.each(errors, error => {
                const $ref = this.findRef(error.field);

                if ($ref && this._.has($ref, 'errorMessagesArray')) {
                    $ref.errorMessagesArray.push(error.message);
                    $ref.$on('input', () => ($ref.errorMessagesArray = []));
                }
            });
        },
        setFormData(data) {
            for (const key in data) {
                this.form[`${key}`] = data[`${key}`];
            }
        },
        onLoadHistory(data) {
            this.form = new this.model(data);
        },
        async getSubmissionDialog() {
            return null;
        },
        async submit(params = {}) {
            try {
                const result = await this.onSubmitFn(params);
                this.onSuccess(result);
                return true;
            } catch (error) {
                this.handleError(error, () => this.submit(params));
                this.onError(error);
            }
        },
        async onSubmitFn({ skipDialog = false }) {
            await this.beforeSubmit();

            if (!skipDialog) {
                const dialogOptions = await this.getSubmissionDialog();
                if (dialogOptions) {
                    return dialog(dialogOptions);
                }
            }

            let payload = this.form;

            if (payload instanceof this.model) {
                payload = payload.$toPayload();
            }

            let response = null;

            if (this.formRequest) {
                response = await this.formRequest(this.form, this.getRequestConfig());
            } else {
                if (this.editing) {
                    this.model_id = this.editing;

                    if (this.updateRequestMethod === 'PUT') {
                        response = await this.model.api.put(payload, this.getRequestConfig());
                    } else if (this.updateRequestMethod === 'PATCH') {
                        let originalPayload = this.original;

                        if (looksLikeBaseModel(originalPayload) && this.form instanceof this.model) {
                            originalPayload = this.form.$toPayload(originalPayload);
                        }

                        payload = diff(originalPayload, payload, {
                            retainArrayValues: true,
                            removeEmptyObjects: true,
                            removeNull: true
                        });

                        response = await this.model.api.patch(this.model_id, payload, this.getRequestConfig());
                    }
                } else {
                    response = await this.model.api.post(this.form, this.getRequestConfig());
                }
            }

            this.$emit('input:response', response);

            this.onDeleteModel();

            let data = null;

            if (response.data && 'id' in response.data) {
                this.model_id = response.data.id;
                data = response.data;
            } else if (response.data && 'created_id' in response.data) {
                this.model_id = response.data.created_id;
                data = (await this.model.api.get(this.model_id)).data;
            }

            this.form.id = this.model_id;

            await this.afterSubmit();

            if (this.updateCacheWithGetAfterSubmit) {
                data = (await this.model.api.get(this.model_id)).data;
            }

            this.model.insertOrUpdate({ data: [data] });

            await this.afterSuccess();
        },
        onDeleteModel() {
            this.model.delete(this.model_id);
        },
        async beforeSubmit() {
            return Promise.resolve();
        },
        async afterSubmit() {
            return Promise.resolve();
        },
        async afterSuccess() {
            if (this.returnId) {
                this.$emit('input', this.model_id);
                this.onSuccess(this.model_id);
                this.resetForm();
                return;
            }

            if (this.returnFields && Array.isArray(this.returnFields)) {
                let fields = {};

                for (const field of this.returnFields) {
                    fields[`${field}`] = this._.get(this.form, field);
                }

                this.$emit('input', fields);
                this.onSuccess(fields);
                this.resetForm();
                return;
            }

            this.form = this.model.find(this.model_id);

            if (this.returnModel) {
                this.$emit('input', this.form);
            } else {
                this.$emit('input', this.form.id);
            }

            this.onSuccess(this.form);
            this.resetForm();
        },
        getRequestConfig() {
            const config = this.requestConfig;

            if (this.requestParams) {
                config.params = this.requestParams;
            }

            return config;
        }
    }
};
